/**
 * /community — announcements and polls from TRF, and the outside groups
 * where parents already talk (2026-09-10 rebuild; #2174).
 *
 * #2174 (ruling, Mario 2026-10-01): Community belongs to TRF alone. TRF posts
 * announcements and polls; families read, react and answer polls, and no
 * longer send notes, so there is no composer here and no "Only my notes"
 * view. A note published before the ruling still reads in the feed.
 *
 * What this page is NOT any more: a categorised, diagnosis-tagged forum with
 * Parent / Kids / Siblings spaces. The research behind the rebuild
 * (docs/superpowers/specs/2026-09-10-community-wall-research.md) found no
 * peer organisation running that shape; the ones that offer peer connection
 * send families to vetted groups on the platforms they already use.
 *
 * Shape: eyebrow · the standard toolbar (search · filter) · one feed
 * (announcements and polls, newest first) · "Where families already talk" ·
 * the crisis callout. Filter is date only.
 *
 * w1023c: the server sends only the announcements whose audience includes
 * this family and whose expiry day has not ended in the zone set in the
 * parent's profile (TRF's clock when there is none). The one pinned announcement leads the feed with a
 * "Pinned" mark. Each feed page the family sees is reported once, in one
 * request, so staff can read "Seen by N of M families".
 */
import { useEffect, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MessageCircle } from 'lucide-react';
import { CardSkeleton } from '@/components/ui/card-skeleton';
import { PageHead, PageToolbar, Pagination } from '@/components/editorial';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { ErrorStateCard } from '@/components/ui/ErrorStateCard';
import { NAV_KEY } from '@/i18n/keys/nav';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/services/api';
import type { CommunityPost, CommunityFeedParams } from '@/types/api';
import type { PollFeedItem } from '@/types/polls';
import { CommunityPostCard } from '@/components/community/CommunityPostCard';
import { announcementApi, type Announcement } from '@/components/community/announcementApi';
import { CommunityFilterPanel } from '@/components/community/CommunityFilterPanel';
import { CommunityGroupsSection } from '@/components/community/CommunityGroupsSection';
import PollCard from '@/components/community/PollCard';
import { CrisisCallout } from '@/components/common/CrisisCallout';
import {
  DEFAULT_COMMUNITY_FILTERS,
  isCommunityFiltered,
  pinnedFirst,
  type CommunityFilters,
  type CommunityWhen,
  type ReactionKey,
} from '@/lib/community-display';

import { quickPollItems } from '@/components/dashboard/checkinTasks';

const PAGE_SIZE = 10;
/** Today's polls lane (DashboardPage) reads this same key; keep the two equal (#2389). */
const POLLS_FEED_QUERY_KEY = ['polls', 'feed'] as const;
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const MONTH_MS = 30 * 24 * 60 * 60 * 1000;

/** The same date scope the server applies to the feed, for the polls beside it. */
function inWhen(createdAt: string, when: CommunityWhen, now = Date.now()): boolean {
  const t = new Date(createdAt).getTime();
  if (when === 'week') return t >= now - WEEK_MS;
  if (when === 'month') return t >= now - MONTH_MS;
  if (when === 'older') return t < now - MONTH_MS;
  return true;
}

export function CommunityPage() {
  const { t } = useTranslation(['community', 'nav', 'common']);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const userId = user?._id || user?.id || '';

  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<CommunityFilters>(DEFAULT_COMMUNITY_FILTERS);
  const [page, setPage] = useState(1);

  const feedParams: CommunityFeedParams = useMemo(() => ({
    page,
    limit: PAGE_SIZE,
    ...(search.trim() ? { q: search.trim() } : {}),
    ...(filters.when ? { when: filters.when } : {}),
  }), [page, search, filters.when]);
  const feedKey = ['community-posts', feedParams] as const;

  // ── Queries ──────────────────────────────────────────────────────────────

  const {
    data: postsResponse,
    isLoading: postsLoading,
    error: postsError,
  } = useQuery({
    queryKey: feedKey,
    queryFn: () => api.community.posts.getAll(feedParams),
    // A new announcement must show the next time a parent opens Community,
    // not after the app-wide 5-minute staleTime (w1023c L4 review).
    refetchOnMount: 'always',
  });
  // Announcements carry `pinned` since w1023c (`Announcement` = CommunityPost + those fields).
  const posts = useMemo<Announcement[]>(() => postsResponse?.data ?? [], [postsResponse]);
  const pagination = postsResponse?.pagination;

  // Seen by (w1023c): once per announcement per visit, one request per page.
  // A failed report is dropped, never retried in a loop and never shown:
  // "seen" is a staff-side count, not something the family is waiting on.
  // Reader: the staff list (`trfer-community-announcements`) on its next load.
  const reportedSeen = useRef<Set<string>>(new Set());
  useEffect(() => {
    const fresh = posts
      .filter((p) => p.kind === 'announcement' && !reportedSeen.current.has(p._id))
      .map((p) => p._id);
    if (fresh.length === 0) return;
    for (const id of fresh) reportedSeen.current.add(id);
    announcementApi.markSeen(fresh).catch(() => {});
  }, [posts]);

  // #2389: the same key as Today's polls lane (DashboardPage), so a vote here
  // is the count there after in-app navigation, not after a reload.
  const { data: pollFeedData } = useQuery({
    queryKey: POLLS_FEED_QUERY_KEY,
    queryFn: () => api.polls.feed(),
    staleTime: 30_000,
  });
  // Quick polls only (2026-10-23, "Check-ins move to Today"): a check-in or
  // custom form send is a to-do row on Today (CheckinTaskRows), never a card here.
  const pollFeedItems = useMemo<PollFeedItem[]>(() => quickPollItems(pollFeedData?.data ?? []), [pollFeedData]);

  // ── Merged feed: posts + polls sorted newest-first ──────────────────────
  type FeedPost = { kind: 'post'; sort: string; post: Announcement };
  type FeedPoll = { kind: 'poll'; sort: string; item: PollFeedItem };
  type FeedItem = FeedPost | FeedPoll;

  const mergedFeed = useMemo<FeedItem[]>(() => {
    const postItems: FeedItem[] = posts.map((p) => ({ kind: 'post' as const, sort: p.created_at, post: p }));
    // A poll answers a search or a date scope the same way a post does.
    const q = search.trim().toLowerCase();
    const pollItems: FeedItem[] = pollFeedItems
      .filter((pf) => pf.poll.published_at != null)
      .filter((pf) => inWhen(pf.poll.published_at!, filters.when))
      .filter((pf) => !q || pf.poll.title.toLowerCase().includes(q))
      .map((pf) => ({ kind: 'poll' as const, sort: pf.poll.published_at!, item: pf }));
    const byDate = [...postItems, ...pollItems].sort(
      (a, b) => new Date(b.sort).getTime() - new Date(a.sort).getTime(),
    );
    // The pinned announcement leads, above polls too (w1023c, `(?) one-pin`).
    return pinnedFirst(byDate, (it) => it.kind === 'post' && it.post.pinned === true);
  }, [posts, pollFeedItems, search, filters.when]);

  // ── Mutations ────────────────────────────────────────────────────────────

  type ReactVars = { id: string; reaction: ReactionKey };
  // #2426: the in-flight reactions, per post. Only that one button ignores a
  // second tap; nothing is `disabled`, so focus stays where the family is.
  const [pendingReactions, setPendingReactions] = useState<Readonly<Record<string, readonly ReactionKey[]>>>({});
  const markReactionPending = (id: string, reaction: ReactionKey, on: boolean) =>
    setPendingReactions((prev) => {
      const list = prev[id] ?? [];
      if (list.includes(reaction) === on) return prev;
      const next = on ? [...list, reaction] : list.filter((r) => r !== reaction);
      const rest = { ...prev };
      if (next.length > 0) rest[id] = next;
      else delete rest[id];
      return rest;
    });
  const applyOptimistic = (id: string, reaction: ReactionKey, add: boolean) => {
    queryClient.setQueryData(feedKey, (old: { data?: CommunityPost[] } | undefined) => {
      if (!old?.data) return old;
      return {
        ...old,
        data: old.data.map((p) => {
          if (p._id !== id) return p;
          const list = p.reactions?.[reaction] ?? [];
          const next = add ? [...list, userId] : list.filter((uid) => uid !== userId);
          return { ...p, reactions: { ...p.reactions, [reaction]: next } };
        }),
      };
    });
  };

  const reactMutation = useMutation({
    mutationFn: ({ id, reaction }: ReactVars) => api.community.posts.react(id, reaction),
    onMutate: async ({ id, reaction }) => {
      markReactionPending(id, reaction, true);
      await queryClient.cancelQueries({ queryKey: feedKey });
      const prevData = queryClient.getQueryData(feedKey);
      applyOptimistic(id, reaction, true);
      return { prevData };
    },
    onError: (_err, _vars, context) => {
      if (context?.prevData) queryClient.setQueryData(feedKey, context.prevData);
      toast({ title: t('toast.reactAddFailed'), description: t('common:states.pleaseTryAgain'), variant: 'destructive' });
    },
    onSettled: (_data, _err, { id, reaction }) => {
      markReactionPending(id, reaction, false);
      return queryClient.invalidateQueries({ queryKey: ['community-posts'] });
    },
  });

  const unreactMutation = useMutation({
    mutationFn: ({ id, reaction }: ReactVars) => api.community.posts.unreact(id, reaction),
    onMutate: async ({ id, reaction }) => {
      markReactionPending(id, reaction, true);
      await queryClient.cancelQueries({ queryKey: feedKey });
      const prevData = queryClient.getQueryData(feedKey);
      applyOptimistic(id, reaction, false);
      return { prevData };
    },
    onError: (_err, _vars, context) => {
      if (context?.prevData) queryClient.setQueryData(feedKey, context.prevData);
      toast({ title: t('toast.reactRemoveFailed'), description: t('common:states.pleaseTryAgain'), variant: 'destructive' });
    },
    onSettled: (_data, _err, { id, reaction }) => {
      markReactionPending(id, reaction, false);
      return queryClient.invalidateQueries({ queryKey: ['community-posts'] });
    },
  });

  // ── Handlers ─────────────────────────────────────────────────────────────

  function handleReaction(postId: string, reaction: ReactionKey, currentReactors: string[]) {
    if (pendingReactions[postId]?.includes(reaction)) return;
    if (currentReactors.includes(userId)) {
      unreactMutation.mutate({ id: postId, reaction });
    } else {
      reactMutation.mutate({ id: postId, reaction });
    }
  }

  function handleFiltersChange(next: CommunityFilters) {
    setFilters(next);
    setPage(1);
  }

  function handleSearchChange(q: string) {
    setSearch(q);
    setPage(1);
  }

  const loading = postsLoading;
  const error = postsError;
  const isEmpty = mergedFeed.length === 0;
  const narrowed = isCommunityFiltered(filters) || search.trim() !== '';
  const firstPostIndex = mergedFeed.findIndex((it) => it.kind === 'post');

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="container max-w-4xl mx-auto py-6 px-4 space-y-6">
      <header className="space-y-4 pb-3.5 border-b border-border/60">
        <PageHead eyebrowKey={NAV_KEY.announcements} />
        <PageToolbar
          label={t(NAV_KEY.announcements)}
          search={{ value: search, onChange: handleSearchChange, placeholder: t('page.searchPlaceholder') }}
          filter={{
            active: isCommunityFiltered(filters),
            label: t('page.filterLabel'),
            panel: <CommunityFilterPanel filters={filters} onChange={handleFiltersChange} />,
          }}
        />
      </header>

      {/* TRF groups first (w1023c L3, Victor 2026-10-23): the groups TRF runs
          are the one place families talk to each other, so they lead. */}
      <CommunityGroupsSection />

      {/* Feed — announcements and polls merged by date */}
      {loading && <CardSkeleton count={3} />}
      {error && <ErrorStateCard message={t('page.loadError')} />}
      {!loading && !error && isEmpty && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-14 text-center">
            <MessageCircle className="h-10 w-10 text-stevens-ink-3 mb-3" />
            {narrowed ? (
              <p className="text-body text-stevens-ink-3">{t('page.empty.narrowed')}</p>
            ) : (
              <>
                <p className="text-body font-medium">{t('page.empty.noneTitle')}</p>
                <p className="text-body text-stevens-ink-3">{t('page.empty.announcementsBody')}</p>
              </>
            )}
          </CardContent>
        </Card>
      )}
      {!loading && !error && !isEmpty && (
        <div className="space-y-4">
          {mergedFeed.map((it, i) =>
            it.kind === 'post' ? (
              <CommunityPostCard
                key={`post-${it.post._id}`}
                post={it.post}
                userId={userId}
                onReact={handleReaction}
                pendingReactions={pendingReactions[it.post._id]}
                showReactionDecision={i === firstPostIndex}
              />
            ) : (
              <PollCard
                key={`poll-${it.item.poll._id}`}
                item={it.item}
                onVote={(answers) =>
                  api.polls.vote(it.item.poll._id, answers).then(() => {
                    void queryClient.invalidateQueries({ queryKey: POLLS_FEED_QUERY_KEY });
                  })
                }
              />
            ),
          )}
          {pagination && (
            <Pagination
              className="pt-4"
              page={pagination.page}
              limit={pagination.limit}
              total={pagination.total}
              itemsKey="common:pagination.posts"
              onPageChange={setPage}
            />
          )}
        </div>
      )}

      {/* Crisis card — Community is the most likely surface where a parent
          reads late at night when things are hardest. The safety rule says AI
          is never a crisis resource; only the three human contacts below. */}
      <CrisisCallout />
    </div>
  );
}
